import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { StageGrid } from '../components/StageGrid.js';
import { Listeners } from '../components/Listeners.js';
import { RoomHeader } from '../components/RoomHeader.js';
import { SoundGate } from '../components/SoundGate.js';
import { ReactionLayer } from '../components/ReactionLayer.js';
import { ReactionBar } from '../components/ReactionBar.js';
import { HandQueue } from '../components/HandQueue.js';
import { haptic } from '../lib/haptics.js';
import { sendReaction, type IncomingReaction, type ReactionId } from '../lib/reactions.js';
import * as api from '../lib/api.js';
import type { PublicRoom, Presence } from '../lib/api.js';
import type { RoomState } from '../lib/useRoom.js';
import type { Room } from 'livekit-client';

export function Stage({
  room,
  state,
  liveRoom,
  isModerator,
  canPresent,
  canBroadcast,
  onToggleMic,
  onToggleCamera,
  onToggleScreen,
  onLeave,
  onEnableAudio,
  reactions,
  pushReaction,
}: {
  room: PublicRoom;
  state: RoomState;
  liveRoom: { current: Room | null };
  isModerator: boolean;
  /** Server-granted `room:present` — cameras and screen share. */
  canPresent: boolean;
  /** Server-granted `room:broadcast` — starting and stopping the recording. */
  canBroadcast: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreen: () => void;
  onLeave: () => void;
  onEnableAudio: () => void;
  reactions: IncomingReaction[];
  pushReaction: (reaction: IncomingReaction) => void;
}) {
  const [presence, setPresence] = useState<Presence | null>(null);
  const [handRaised, setHandRaised] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  /* Held locally as well as polled, so the button reacts on press instead of
     waiting up to five seconds for the next presence poll to agree. */
  const [recording, setRecording] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  /* A ref as well as state: the presence poll's closure is created once and
     would otherwise read `recBusy` as it was on mount, forever. */
  const recBusyRef = useRef(false);
  const [recError, setRecError] = useState<string | null>(null);

  /* Presence has to come from the server: the audience is `hidden`, so the SFU
     never tells this client they exist. Polled rather than pushed — a count a
     few seconds stale is fine, and it avoids a second socket per listener. */
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const next = await api.getPresence(room.id);
        if (!alive) return;
        setPresence(next);
        // The server is the authority — but not mid-request, or the poll would
        // undo the optimistic state before the call it is racing has landed.
        if (!recBusyRef.current) setRecording(next.recording);
      } catch {
        /* transient; the previous count stays rather than blanking the room */
      }
    };
    void poll();
    const timer = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [room.id]);

  const react = useCallback(
    (id: ReactionId) => {
      const current = liveRoom.current;
      if (!current) return;
      if (!sendReaction(current, id)) return; // rate limited
      haptic('tick');
      // Show your own reaction immediately: the round trip is real, and a
      // button that appears to do nothing gets pressed again.
      pushReaction({ id, from: 'you' });
    },
    [liveRoom, pushReaction],
  );

  const toggleHand = useCallback(async () => {
    const next = !handRaised;
    haptic('tick');
    setHandRaised(next);
    try {
      await api.raiseHand(room.id, next);
    } catch {
      haptic('snag');
      setHandRaised(!next);
    }
  }, [handRaised, room.id]);

  /**
   * Start or stop the recording.
   *
   * Optimistic on the way in, because egress takes a couple of seconds to
   * accept and a host who sees nothing happen presses again — which is how you
   * end up with two egresses on one show. `busy` blocks the second press, and
   * the next presence poll replaces the guess with the truth either way.
   */
  const toggleRecording = useCallback(async () => {
    if (recBusy) return;
    const next = !recording;
    setRecBusy(true);
    recBusyRef.current = true;
    haptic('press');
    setRecording(next);
    try {
      if (next) await api.startShow(room.id);
      else await api.stopShow(room.id);
      setPresence(await api.getPresence(room.id));
    } catch (error) {
      haptic('snag');
      setRecording(!next);
      /*
       * 412 is the one failure a host can act on: egress refuses to start
       * against a room where nobody is publishing, rather than writing an empty
       * file. Say that plainly instead of showing a status code.
       */
      const status = error instanceof api.ApiError ? error.status : 0;
      setRecError(
        status === 412
          ? 'Nobody is on stage yet — unmute or turn a camera on, then start recording.'
          : error instanceof Error
            ? error.message
            : 'Could not change the recording.',
      );
      setTimeout(() => setRecError(null), 6000);
    } finally {
      setRecBusy(false);
      recBusyRef.current = false;
    }
  }, [recBusy, recording, room.id]);

  const moderate = useCallback(
    async (sub: string, action: 'promote-speaker' | 'demote') => {
      setBusy(sub);
      try {
        await api.stageAction(room.id, action, sub);
        setPresence(await api.getPresence(room.id));
      } catch {
        haptic('snag');
      } finally {
        setBusy(null);
      }
    },
    [room.id],
  );

  /*
   * Connection state first, then device trouble.
   *
   * `state.error` was being set by the mic, camera and screen-share handlers
   * and rendered NOWHERE — so a denied microphone looked exactly like a working
   * one that nobody could hear. Connection state still outranks it: "you left
   * the room" explains a dead mic better than the mic error does.
   */
  const notice =
    state.phase === 'reconnecting'
      ? 'Connection dropped. Reconnecting…'
      : state.phase === 'disconnected'
        ? state.removed
          ? 'A host removed you from this room.'
          : 'You left the room.'
        : state.error;

  const noticeIsBad = state.removed || (state.phase === 'connected' && Boolean(state.error));

  const hands = presence?.raisedHands ?? [];

  /* Camera needs BOTH the grant and a video-enabled room — the SFU only puts
     CAMERA in canPublishSources when room.videoEnabled, so showing the button
     on an audio show would offer something the server refuses. */
  const showCamera = canPresent && room.videoEnabled;
  /* getDisplayMedia does not exist on mobile Safari or Android Chrome. Checking
     for it beats showing a button that can only fail. */
  const showScreen =
    canPresent && typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia);

  return (
    <>
      <ReactionLayer feed={reactions} />

      {/* The one thing allowed to interrupt: a silent room looks fine. */}
      {state.audioBlocked ? <SoundGate onEnable={onEnableAudio} /> : null}

      <main class={`room${state.audioBlocked ? ' room--gated' : ''}`}>
        <RoomHeader
          title={room.title}
          roomId={room.id}
          live={presence?.live ?? false}
          recording={presence?.recording ?? false}
        />

        {notice ? (
          <p class={`notice${noticeIsBad ? ' notice--bad' : ''}`}>{notice}</p>
        ) : null}

        <section class="room__stage">
          {state.stage.length ? (
            <StageGrid people={state.stage} hostSubs={room.moderators} />
          ) : (
            <div class="empty">
              <div class="empty__pulse" aria-hidden="true" />
              <p class="empty__title">
                {state.canSpeak ? 'You have the stage' : 'Nobody is speaking yet'}
              </p>
              <p class="t-foot">
                {state.canSpeak
                  ? 'Unmute when you are ready to start.'
                  : "You'll hear them the moment they begin."}
              </p>
            </div>
          )}
        </section>

        <section class="room__audience">
          <Listeners count={presence?.listening ?? 0} known={[]} />
        </section>

        {isModerator && hands.length ? (
          <section class="room__hands">
            <p class="cell__label">
              Hands up <span class="chip">{hands.length}</span>
            </p>
            <HandQueue
              hands={hands}
              busy={busy}
              onPromote={(sub) => moderate(sub, 'promote-speaker')}
              onDismiss={(sub) => moderate(sub, 'demote')}
            />
          </section>
        ) : null}
      </main>

      {/* Thumb zone (§8): what a listener actually touches, pinned to the
          bottom, never scrolled away from. */}
      <div class="dock">
        {recError ? <p class="dock__error" role="status">{recError}</p> : null}
        <ReactionBar onReact={react} />
        <div class="dock__actions">
          {canBroadcast ? (
            <button
              class={`dock__rec${recording ? ' dock__rec--on' : ''}`}
              onClick={toggleRecording}
              disabled={recBusy}
              aria-pressed={recording}
              title={recording ? 'Stop recording' : 'Record this show'}
            >
              <span class="dock__rec-dot" aria-hidden="true" />
              {recording ? 'Stop' : 'Record'}
            </button>
          ) : null}
          {state.canSpeak ? (
            <button
              class={`dock__mic${state.micOn ? ' dock__mic--on' : ''}`}
              onClick={onToggleMic}
              aria-pressed={state.micOn}
            >
              <MicIcon on={state.micOn} />
              {state.micOn ? 'Mute' : 'Unmute'}
            </button>
          ) : (
            <button
              class={`dock__hand${handRaised ? ' dock__hand--up' : ''}`}
              onClick={toggleHand}
              aria-pressed={handRaised}
            >
              <span aria-hidden="true">✋</span>
              {handRaised ? 'Lower hand' : 'Raise hand'}
            </button>
          )}
          {showCamera ? (
            <button
              class={`dock__media${state.cameraOn ? ' dock__media--on' : ''}`}
              onClick={onToggleCamera}
              aria-pressed={state.cameraOn}
              title={state.cameraOn ? 'Turn camera off' : 'Turn camera on'}
            >
              <CameraIcon on={state.cameraOn} />
              {/* A noun, not an action verb, unlike the mic beside it: the icon
                  carries a slash when off and the button fills when on, so the
                  state is already legible without the label changing width
                  under the thumb. `title` and aria-pressed carry the action. */}
              <span class="dock__media-label">Camera</span>
            </button>
          ) : null}

          {showScreen ? (
            <button
              class={`dock__media${state.screenOn ? ' dock__media--on' : ''}`}
              onClick={onToggleScreen}
              aria-pressed={state.screenOn}
              title={state.screenOn ? 'Stop sharing' : 'Share your screen'}
            >
              <ScreenIcon />
              <span class="dock__media-label">{state.screenOn ? 'Sharing' : 'Share'}</span>
            </button>
          ) : null}

          <button class="dock__leave" onClick={onLeave}>
            Leave
          </button>
        </div>
      </div>
    </>
  );
}

function CameraIcon({ on }: { on: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="m22 8-6 4 6 4V8Z" />
      {on ? null : <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

function ScreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function MicIcon({ on }: { on: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3" />
      {on ? null : <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}
